import { useMemo, useState, useEffect, useCallback } from "react";
import { useActionMutations } from "~/pages/administration/workflows-page/hooks/apis/use-action-mutations";
import { useWorkflowStatusMutations } from "~/pages/administration/workflows-page/hooks/apis/use-workflow-status-mutations";
import type { UpdateWorkflowState } from "~/pages/administration/workflows-page/workflow-creation-context-provider";
import type { CreateLocalActionRequest } from "~/pages/administration/workflows-page/workflows-dialog/views/action-view/hooks/use-action-view";
import type { CreateStatusRequest } from "~/types/api";
import { useStatusCodeChanges, type StatusCodeChange } from "./use-status-code-changes";
import type { StatusRow, StatusUnion } from "../status-view";

type ChangesSummary = {
  toCreate: StatusUnion[];
  toUpdate: StatusUnion[];
  toDelete: number[];
  hasChanges: boolean;
};

type Props = {
  isEditMode?: boolean;
  workflowId?: number;
  workflowStatuses: StatusUnion[];
  committedStatuses: StatusUnion[];
  allActions: CreateLocalActionRequest[];
  onStatusDeleted?: (statusCode: number) => void;
};

export const useStatusView = ({
  isEditMode,
  workflowId,
  workflowStatuses,
  committedStatuses,
  allActions,
  onStatusDeleted,
}: Props) => {
  const {
    statuses: apiStatuses,
    isLoading: isLoadingStatuses,
    createStatus,
    updateStatus,
    deleteStatus,
    isCreating,
    isUpdating,
    isDeleting,
  } = useWorkflowStatusMutations(workflowId, isEditMode);

  const { actions: apiActions, isLoading: isLoadingActions } = useActionMutations(
    isEditMode && workflowId ? [workflowId] : []
  );

  const actionCountByStatus = useMemo(() => {
    const linkedCodesPerAction = isEditMode
      ? apiActions.map((a) => (a.statuses ?? []).map((s) => s.statusCode))
      : allActions.map((a) => a.statusCodes ?? []);

    const counts = new Map<number, number>();
    linkedCodesPerAction.forEach((codes) =>
      new Set(codes).forEach((code) => counts.set(code, (counts.get(code) ?? 0) + 1))
    );
    return counts;
  }, [isEditMode, apiActions, allActions]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [localStatuses, setLocalStatuses] = useState<StatusUnion[]>(
    workflowStatuses.length > 0 ? workflowStatuses : []
  );

  const [originalStatusesMap, setOriginalStatusesMap] = useState<Map<number, StatusUnion>>(
    committedStatuses.length > 0 ? new Map(committedStatuses.map((s) => [s.code, s])) : new Map()
  );
  const [editedStatusCodes, setEditedStatusCodes] = useState<StatusCodeChange[]>([]);

  const { resolveEditedStatusCodes, showCodeChangeToast, optimisticUpdateStatusCode } =
    useStatusCodeChanges({
      editedStatusCodes,
      setEditedStatusCodes,
      localStatuses,
      originalStatusesMap,
    });

  useEffect(() => {
    if (isEditMode && apiStatuses.length > 0) {
      const statusMap = new Map<number, StatusUnion>();
      const mappedStatuses = apiStatuses.map((s) => {
        const status = { code: s.code, name: s.name };
        statusMap.set(s.code, status);
        return status;
      });

      setOriginalStatusesMap(statusMap);
      setLocalStatuses(mappedStatuses);
    }
  }, [isEditMode, workflowId, apiStatuses]);

  useEffect(() => {
    if (!isEditMode && originalStatusesMap.size === 0 && committedStatuses.length > 0) {
      const statusMap = new Map<number, StatusUnion>();
      committedStatuses.forEach((s) => statusMap.set(s.code, s));
      setOriginalStatusesMap(statusMap);
    }
  }, [isEditMode, originalStatusesMap.size, committedStatuses]);

  const data = useMemo<StatusRow[]>(() => {
    return localStatuses
      .map((status, index) => ({
        id: `${status.name}-${index}`,
        name: status.name,
        statusCode: status.code,
        actionsCount: actionCountByStatus.get(status.code) ?? 0,
        originalObj: status,
      }))
      .sort((a, b) => a.statusCode - b.statusCode);
  }, [localStatuses]);

  const selectedStatus = useMemo(() => {
    if (selectedIndex === null || !data[selectedIndex]) return null;
    return data[selectedIndex].originalObj;
  }, [selectedIndex, data]);

  const takenCodes = useMemo(() => {
    if (selectedIndex === null) return [];
    return data.filter((_, index) => index !== selectedIndex).map((d) => d.statusCode);
  }, [data, selectedIndex]);

  const changesSummary = useMemo<ChangesSummary>(() => {
    const toCreate: StatusUnion[] = [];
    const toUpdate: StatusUnion[] = [];
    const toDeleteSet = new Set<number>();

    const changedOldCodes = new Set(editedStatusCodes.map((e) => e.oldCode));
    localStatuses.forEach((status) => {
      const original = originalStatusesMap.get(status.code);
      if (!original) {
        toCreate.push(status);
      } else if (original.name !== status.name) {
        toUpdate.push(status);
      }
    });

    originalStatusesMap.forEach((_, code) => {
      const existsInLocal = localStatuses.some((s) => s.code === code);
      if (!existsInLocal) {
        toDeleteSet.add(code);
      } else if (changedOldCodes.has(code)) {
        toDeleteSet.add(code);
        if (!toCreate.some((s) => s.code === code)) {
          toCreate.push(localStatuses.find((s) => s.code === code)!);
        }
      }
    });

    const filteredUpdate = toUpdate.filter((s) => !toDeleteSet.has(s.code));

    let hasDeletions = false;
    if (!isEditMode) {
      hasDeletions = originalStatusesMap.size !== localStatuses.length;
    }

    return {
      toCreate,
      toUpdate: filteredUpdate,
      toDelete: Array.from(toDeleteSet),
      hasChanges:
        toCreate.length > 0 ||
        filteredUpdate.length > 0 ||
        toDeleteSet.size > 0 ||
        hasDeletions ||
        editedStatusCodes.length > 0,
    };
  }, [localStatuses, originalStatusesMap, editedStatusCodes, isEditMode]);

  const handleOnClickAdd = useCallback(() => {
    const maxCode = localStatuses.length > 0 ? Math.max(...localStatuses.map((d) => d.code)) : 0;
    const nextCode = maxCode + 1;
    setLocalStatuses((prev) => [...prev, { code: nextCode, name: `Status ${nextCode}` }]);
    setSelectedIndex(localStatuses.length);
  }, [localStatuses]);

  const handleInputSave = useCallback(
    (updatedStatus: CreateStatusRequest) => {
      if (selectedIndex === null) return;
      const currentStatus = data[selectedIndex]?.originalObj;
      if (!currentStatus) return;

      resolveEditedStatusCodes(currentStatus.code, updatedStatus.code);

      const newStatuses = data.map((d) => d.originalObj);
      newStatuses[selectedIndex] = updatedStatus;
      setLocalStatuses(newStatuses);
    },
    [selectedIndex, data, resolveEditedStatusCodes]
  );

  const handleDelete = useCallback(() => {
    if (selectedIndex === null) return;
    const deletedStatus = data[selectedIndex]?.originalObj;
    const newStatuses = data
      .filter((_, index) => index !== selectedIndex)
      .map((d) => d.originalObj);

    setLocalStatuses(newStatuses);
    setSelectedIndex(null);

    if (deletedStatus && onStatusDeleted) {
      onStatusDeleted(deletedStatus.code);
    }
  }, [selectedIndex, data, onStatusDeleted]);

  const handleSaveGlobal = useCallback(
    async (onClose?: () => void, updateWorkflowState?: UpdateWorkflowState) => {
      if (isEditMode) {
        const failedCodes = new Set<number>();
        const { toCreate, toUpdate, toDelete } = changesSummary;
        const localEdditedStatusCodesResponseSuccess = new Set<number>();

        if (toDelete.length > 0) {
          for (const code of toDelete) {
            const editedChange = editedStatusCodes.find((change) => change.oldCode === code);
            try {
              await deleteStatus({
                workflowId: workflowId!,
                code,
                showToastMessage: !editedChange,
              });

              if (editedChange) {
                localEdditedStatusCodesResponseSuccess.add(code);
              }
            } catch (error) {
              failedCodes.add(code);
              console.error(`Failed to delete status with code ${code}:`, error);
            }
          }
        }

        if (toCreate.length > 0) {
          for (const status of toCreate) {
            const matchesNew = editedStatusCodes.find((c) => c.newCode === status.code);
            const oldCodeIsOriginal = matchesNew && originalStatusesMap.has(matchesNew.oldCode);
            if (
              oldCodeIsOriginal &&
              !localEdditedStatusCodesResponseSuccess.has(matchesNew!.oldCode)
            ) {
              continue;
            }
            try {
              await createStatus({
                workflowId: workflowId!,
                status,
                showToastMessage: !oldCodeIsOriginal,
              });
              if (matchesNew) {
                localEdditedStatusCodesResponseSuccess.add(status.code);
              }
            } catch (error) {
              failedCodes.add(status.code);
              console.error(`Failed to create status with code ${status.code}:`, error);
            }
          }
        }

        if (toUpdate.length > 0) {
          for (const status of toUpdate) {
            const matchesNew = editedStatusCodes.find((c) => c.newCode === status.code);
            const isSwap = matchesNew && toUpdate.some((s) => s.code === matchesNew.oldCode);
            if (
              matchesNew &&
              !isSwap &&
              !localEdditedStatusCodesResponseSuccess.has(matchesNew.oldCode)
            ) {
              continue;
            }
            try {
              await updateStatus({
                workflowId: workflowId!,
                code: status.code,
                name: status.name,
                showToastMessage: !matchesNew,
              });
              if (matchesNew) {
                localEdditedStatusCodesResponseSuccess.add(matchesNew.oldCode);
                localEdditedStatusCodesResponseSuccess.add(status.code);
              }
            } catch (error) {
              failedCodes.add(status.code);
              console.error(`Failed to update status with code ${status.code}:`, error);
            }
          }
        }

        showCodeChangeToast(localEdditedStatusCodesResponseSuccess);

        const successStatuses = optimisticUpdateStatusCode(failedCodes);
        updateWorkflowState?.("statuses", successStatuses);

        if (failedCodes.size === 0) {
          onClose?.();
        }
      } else {
        updateWorkflowState?.("statuses", localStatuses as CreateStatusRequest[]);
        updateWorkflowState?.("committedStatuses", localStatuses as CreateStatusRequest[]);
        updateWorkflowState?.("currentView", "default");
      }
    },
    [
      isEditMode,
      changesSummary,
      editedStatusCodes,
      originalStatusesMap,
      localStatuses,
      workflowId,
      createStatus,
      updateStatus,
      deleteStatus,
      showCodeChangeToast,
      optimisticUpdateStatusCode,
    ]
  );

  return {
    data,
    selectedStatus,
    takenCodes,
    changesSummary,
    localStatuses,

    selectedIndex,
    setSelectedIndex,
    isPending: isCreating || isUpdating || isDeleting,
    isLoadingStatuses,
    isLoadingActions,

    handleOnClickAdd,
    handleInputSave,
    handleDelete,
    handleSaveGlobal,
  };
};
